import type { FieldPath } from 'react-hook-form';

import { US_STATE_ABBREVIATIONS } from 'constants/USStateTimezones';
import { LoadStopsItem, RateData } from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import type { ManualFieldError } from 'utils/formValidation';
import { normalizeToArray } from 'utils/loadInfoAndBuilding';

import { RateType } from '../../components/RateGrid';
import { isPlsPickupStopType } from '../plsRouteStops';
import {
  PendingReference,
  PlsFormValues,
  PlsOperationalFieldDefinition,
} from '../types';
import {
  plsChargeAccessorialCode,
  plsSpecsAccessorialRates,
} from './plsAccessorialCharges';

const CANADIAN_PROVINCES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
];

const US_ZIP = /^\d{5}(-\d{4})?$/;
const CANADIAN_POSTAL_CODE = /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i;
const MEXICAN_POSTAL_CODE = /^\d{5}$/;

enum PlsStopCountry {
  US = 'US',
  Canada = 'CA',
  Mexico = 'MX',
}

// PLS requires every customer reference field the bill-to has on each new load (drumkit
// pls.RequiredReferenceTypes reads the same list), so an empty one blocks Build Load. While the
// bill-to's list is still loading there is nothing to check yet, so Build Load waits for it.
export function validatePlsRequiredCustomerReferences(
  values: MaybeUndef<Record<string, string>>,
  fieldDefinitions: PlsOperationalFieldDefinition[],
  isLoadingFieldDefinitions = false
): ManualFieldError<PlsFormValues>[] {
  if (isLoadingFieldDefinitions) {
    return [
      {
        path: 'plsOperationalFields',
        message:
          "The bill-to's customer reference fields are still loading. Try Build Load again in a moment.",
      },
    ];
  }
  return fieldDefinitions
    .filter((field) => field.code?.trim() && !values?.[field.code]?.trim())
    .map((field) => ({
      path: `plsOperationalFields.${field.code}` as FieldPath<PlsFormValues>,
      message: `${field.name} is required for this bill-to in PLS.`,
    }));
}

// A reference card with a number but no type would be dropped on save, so submit stops on it.
export function validatePlsPendingReferences(
  pending: MaybeUndef<PendingReference[]>
): ManualFieldError<PlsFormValues>[] {
  return (pending ?? []).flatMap((reference, index) =>
    reference.value.trim()
      ? [
          {
            path: `plsPendingReferences.${index}.value` as FieldPath<PlsFormValues>,
            message: `Pick a type for ${reference.value.trim()}, or remove its card.`,
          },
        ]
      : []
  );
}

// Checks PLS create would reject, run before Build Load so the rep fixes them in place: stop
// addresses, a pickup date in the past, and the customer rate. Required fields are the form's own
// rules; this covers what those rules can't express.
export function validatePlsCreateLoad(
  values: Pick<PlsFormValues, 'stops' | 'rateData'> &
    Partial<Pick<PlsFormValues, 'specifications' | 'accessorials'>>,
  now: Date
): ManualFieldError<PlsFormValues>[] {
  return [
    ...validatePlsStops(normalizeToArray(values.stops) ?? [], now),
    ...validatePlsRate(
      values.rateData,
      values.specifications?.totalDistance?.val ?? 0,
      plsSpecsAccessorialRates(values.accessorials)
    ),
  ];
}

function validatePlsStops(
  stops: LoadStopsItem[],
  now: Date
): ManualFieldError<PlsFormValues>[] {
  const errors: ManualFieldError<PlsFormValues>[] = [];

  stops.forEach((stop, index) => {
    const path = (field: string) =>
      `stops.${index}.${field}` as FieldPath<PlsFormValues>;
    const country = plsStopCountry(stop.address?.country);
    const state = stop.address?.state?.trim().toUpperCase() ?? '';
    const zip = stop.address?.zip?.trim() ?? '';

    if (state && !isValidPlsState(state, country)) {
      errors.push({
        path: path('address.state'),
        message: 'Use the 2-letter state or province code.',
      });
    }
    if (zip && !isValidPlsZip(zip, country)) {
      errors.push({
        path: path('address.zip'),
        message:
          country === PlsStopCountry.Canada
            ? 'Use a postal code like A1A 1A1.'
            : 'Use a 5-digit zip code.',
      });
    }

    if (
      index === 0 &&
      isPlsPickupStopType(stop.stopType) &&
      isBeforeToday(stop.readyTime, now)
    ) {
      errors.push({
        path: path('readyTime'),
        message: 'Pickup date is in the past.',
      });
    }
  });

  return errors;
}

function validatePlsRate(
  rate: MaybeUndef<RateData>,
  miles: number,
  specsAccessorialRates: Map<string, number>
): ManualFieldError<PlsFormValues>[] {
  if (!rate) {
    return [];
  }

  const errors: ManualFieldError<PlsFormValues>[] = [];
  const amount = (
    path: FieldPath<PlsFormValues>,
    value: MaybeUndef<Maybe<number>>,
    max?: number
  ) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return;
    }
    if (value < 0) {
      errors.push({ path, message: 'Enter an amount of 0 or more.' });
    } else if (max !== undefined && value > max) {
      errors.push({ path, message: `Enter at most ${max}.` });
    }
  };

  amount('rateData.customerLineHaulRate', rate.customerLineHaulRate);
  amount('rateData.fscPercent', rate.fscPercent, 100);
  amount('rateData.fscPerMile', rate.fscPerMile);
  amount('rateData.fscFlatRate', rate.fscFlatRate);

  // PLS computes a percent surcharge from the line haul it's sent with.
  const hasLineHaul =
    (rate.customerLineHaulRate ?? 0) > 0 ||
    (rate.customerLineHaulCharge?.val ?? 0) > 0;
  if ((rate.fscPercent ?? 0) > 0 && !hasLineHaul) {
    errors.push({
      path: 'rateData.customerLineHaulRate',
      message: 'Enter the line haul rate for a percent fuel surcharge.',
    });
  } else if (
    (rate.fscPercent ?? 0) > 0 &&
    rate.customerRateType === RateType.Distance &&
    !(miles > 0)
  ) {
    // PLS needs the line haul in dollars for a percent line, and only knows the miles after
    // create, so a per-mile rate needs the trip's miles or a fuel surcharge per mile. The card's
    // line haul charge doesn't count: the rate grid derives it and Build Load doesn't send it.
    errors.push({
      path: 'rateData.fscPercent',
      message:
        'PLS needs the trip miles for a percent fuel surcharge on a per-mile rate. Enter the miles or use fuel per mile.',
    });
  }

  (normalizeToArray(rate.customerLineItems) ?? []).forEach((item, index) => {
    // A charge for an accessorial picked in Specs can stay at 0: create sends that accessorial
    // at PLS's default rate, as it did before the card listed it.
    if (specsAccessorialRates.has(plsChargeAccessorialCode(item))) {
      return;
    }
    if (!(Number(item.ratePerUnitUSD) > 0)) {
      errors.push({
        path: `rateData.customerLineItems.${index}.ratePerUnitUSD` as FieldPath<PlsFormValues>,
        message: 'Enter the charge for this accessorial.',
      });
    }
  });

  return errors;
}

function plsStopCountry(country: MaybeUndef<string>): PlsStopCountry {
  switch ((country ?? '').trim().toUpperCase()) {
    case 'CA':
    case 'CAN':
    case 'CANADA':
      return PlsStopCountry.Canada;
    case 'MX':
    case 'MEX':
    case 'MEXICO':
      return PlsStopCountry.Mexico;
    default:
      return PlsStopCountry.US;
  }
}

function isValidPlsState(state: string, country: PlsStopCountry): boolean {
  switch (country) {
    case PlsStopCountry.Canada:
      return CANADIAN_PROVINCES.includes(state);
    case PlsStopCountry.Mexico:
      return /^[A-Z]{2,3}$/.test(state);
    default:
      return US_STATE_ABBREVIATIONS.includes(state);
  }
}

function isValidPlsZip(zip: string, country: PlsStopCountry): boolean {
  switch (country) {
    case PlsStopCountry.Canada:
      return CANADIAN_POSTAL_CODE.test(zip);
    case PlsStopCountry.Mexico:
      return MEXICAN_POSTAL_CODE.test(zip);
    default:
      return US_ZIP.test(zip);
  }
}

// PLS stop times are wall clock stored in the Date's UTC fields (see the create payload), so the
// calendar day is read in UTC and compared to the rep's today.
function isBeforeToday(value: MaybeUndef<string>, now: Date): boolean {
  if (!value) {
    return false;
  }
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) {
    return false;
  }

  const stopDay = Date.UTC(
    time.getUTCFullYear(),
    time.getUTCMonth(),
    time.getUTCDate()
  );
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return stopDay < today;
}
